import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import OverallPerformance from '../components/InterviewResults/OverallPerformance';
import QuestionAnalysis from '../components/InterviewResults/QuestionAnalysis';
import SkillCard, { Styling } from '../components/InterviewResults/SkillCard';
import { Test } from '../redux/Slices/InterviewSlice/types';
import {
  useLazyGetFullTestDetailsQuery,
  useGetColorCodesQuery
} from '../services/startTestAPI/resultsTestApi';

const InterviewResults = ({
  propsInterviewResults
}: {
  propsInterviewResults?: Test;
}) => {
  const { id, userId } = useParams();
  const navigate = useNavigate();
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [getFullTestResults, { data, isSuccess, isLoading }] =
    useLazyGetFullTestDetailsQuery({});
  const { data: colorCodeData, isSuccess: colorCodeSuccess } =
    useGetColorCodesQuery({}, { refetchOnMountOrArgChange: true });
  const [interviewFullResults, setInterviewFullResults] = useState<Test>(
    {} as Test
  );
  const getMatchedColorCode = (status: string): Styling => {
    if (colorCodeSuccess && colorCodeData?.data) {
      const matchedColorCode = colorCodeData?.data.find(
        (each: Styling) => each.status === status
      );
      if (matchedColorCode) {
        return matchedColorCode;
      }
    }
    return {
      backgroundColor: 'bg-[#bbeaf8]',
      borderColor: 'border-[#58a6f5]',
      badgeBackgroundColor: 'bg-[#cdf7e8]',
      badgeTextColor: 'text-[#000000]',
      status: 'Null'
    };
  };

  useEffect(() => {
    if (isSuccess && !propsInterviewResults) {
      setInterviewFullResults(data?.data);
    } else if (propsInterviewResults) {
      setInterviewFullResults(propsInterviewResults);
    }
  }, [data, isSuccess]);

  useEffect(() => {
    if (propsInterviewResults) return;
    const userTestId = id;
    getFullTestResults({ userTestId, userId });
  }, []);

  if (isLoading) {
    return (
      <div className='flex min-h-screen items-center justify-center bg-gradient-to-br from-indigo-100 via-purple-50 to-blue-100'>
        <div className='text-center'>
          <div className='mx-auto mb-4 h-16 w-16 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent'></div>
          <p className='text-gray-600'>Processing results...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-blue-100 px-4 py-8'>
      <div className='mx-auto max-w-6xl'>
        <div className='mb-6'>
          <button
            onClick={() => navigate('/results')}
            className='group flex items-center gap-2 rounded-lg bg-white px-4 py-2 shadow-md transition-all duration-200 hover:bg-gray-50 hover:shadow-lg'
          >
            <svg
              className='h-5 w-5 text-gray-600 transition-transform duration-200 group-hover:-translate-x-1'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
            >
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M15 19l-7-7 7-7'
              />
            </svg>
            <span className='font-medium text-gray-700'>Back to Results</span>
          </button>
        </div>

        <div className='mb-3 rounded-2xl bg-gradient-to-r from-[#667eea] to-[#764ba2] px-3 py-3 text-center text-white shadow-xl md:mb-8 md:px-6 md:py-8'>
          <h1 className='text-xl font-bold md:mb-2 md:text-4xl'>
            Tech Interview Results - {interviewFullResults?.testSet?.testName}
          </h1>
        </div>

        <div className='rounded-2xl bg-white p-2 shadow-xl md:p-8'>
          <OverallPerformance
            overall={interviewFullResults?.overAllEvaluationResult}
          />

          <div className='mb-8 grid grid-cols-2 gap-3 sm:grid-cols-2 md:gap-6 lg:grid-cols-3 xl:grid-cols-5'>
            <SkillCard
              skill={
                interviewFullResults.overAllEvaluationResult
                  ?.clarityofExpressionState
              }
              label={'Clarity of Expression'}
              colorCodeData={getMatchedColorCode(
                interviewFullResults.overAllEvaluationResult
                  ?.clarityofExpressionState
              )}
            />
            <SkillCard
              skill={
                interviewFullResults.overAllEvaluationResult
                  ?.technicalDepthState
              }
              label={'Technical Depth'}
              colorCodeData={getMatchedColorCode(
                interviewFullResults.overAllEvaluationResult
                  ?.technicalDepthState
              )}
            />
            <SkillCard
              skill={
                interviewFullResults.overAllEvaluationResult
                  ?.practicalExperienceState
              }
              label={'Practical Experience'}
              colorCodeData={getMatchedColorCode(
                interviewFullResults.overAllEvaluationResult
                  ?.practicalExperienceState
              )}
            />
            <SkillCard
              skill={
                interviewFullResults.overAllEvaluationResult
                  ?.problemSolvingApproachState
              }
              label={'Problem Solving'}
              colorCodeData={getMatchedColorCode(
                interviewFullResults.overAllEvaluationResult
                  ?.problemSolvingApproachState
              )}
            />
            <SkillCard
              skill={
                interviewFullResults.overAllEvaluationResult
                  ?.professionalismState
              }
              label={'Professionalism'}
              colorCodeData={getMatchedColorCode(
                interviewFullResults.overAllEvaluationResult
                  ?.professionalismState
              )}
            />
          </div>

          {interviewFullResults?.answers?.map((eachQuestion, index: number) => (
            <QuestionAnalysis
              key={eachQuestion?.questionId || index}
              questionNo={index + 1}
              question={eachQuestion}
              allQuestions={interviewFullResults?.testSet?.questions}
              isPlayingAudio={isPlayingAudio}
              setIsPlayingAudio={setIsPlayingAudio}
              shouldShowIdealAnswer={true}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default InterviewResults;
